/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { Button as ButtonCore, type ButtonProps as ButtonCoreProps, type ButtonRenderProps, type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { IconContext, type IconProps } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface BadgeButtonProps extends Omit<ButtonCoreProps, 'style' | 'className'> {
  /** The size of the badge button. (Default: 20) */
  size?: keyof typeof sizeVariants;
  /** The color of the badge button. (Default: blueLight) */
  color?: keyof typeof fillColorVariants;
  /** Whether the badge button is rounded. (Default: false) */
  isRounded?: boolean;
  /** Whether the badge button is pastel. (Default: false) */
  isPastel?: boolean;
  style?: StyleOrFunction<ButtonRenderProps> | {
    base?: StyleOrFunction<ButtonRenderProps>;
    icon?: StyleOrFunction<ButtonRenderProps>;
    text?: StyleOrFunction<ButtonRenderProps>;
  };
}

const BadgeButtonContext = createContext<ContextValue<BadgeButtonProps, HTMLButtonElement>>({});

const BadgeButton = forwardRef((props: BadgeButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, BadgeButtonContext);

  const {
    size = 20,
    color = 'blueLight',
    isPastel = false,
    isRounded = false,
    style,
    children,
    ...coreProps
  } = props;

  return (
    <ButtonCore
      ref={ref}
      {...applyStylesWithRenderProps<ButtonRenderProps>((state) => stylex.props(
        styles.base,
        isRounded && styles.rounded,
        sizeVariants[size],
        isPastel ? pastelColorVariants[color] : fillColorVariants[color],
        coreProps.isDisabled && styles.disabled,
        resolveComponentStyle(style, state, 'base'),
      ))}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;
        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [
              TextContext,
              {
                size: textSizes[size],
                color: textColors[isPastel ? 'pastel' : 'fill'][color],
                style: [badgeTextStyles.base, resolveComponentStyle(style, renderProps, 'text')],
              },
            ],
            [
              IconContext,
              {
                color: iconColors[isPastel ? 'pastel' : 'fill'][color],
                size: iconSizes[size],
                style: resolveComponentStyle(style, renderProps, 'icon'),
              }
            ]
          ]}>
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const iconSizes: Record<keyof typeof sizeVariants, IconProps['size']> = {
  16: 8,
  20: 12,
  32: 12,
};

const textSizes: Record<keyof typeof sizeVariants, TextProps['size']> = {
  16: 10,
  20: 12,
  32: 13,
};

const textColors: {
  fill: Record<keyof typeof fillColorVariants, TextProps['color']>,
  pastel: Record<keyof typeof pastelColorVariants, TextProps['color']>
} = {
  fill: {
    red: 'white',
    yellow: 'white',
    orange: 'white',
    green: 'white',
    turquoise: 'white',
    blueLight: 'white',
    blueDark: 'white',
    violet: 'white',
    greyDark: 'inverted',
    greyLight: 'inverted',
    disabled: 'disabled',
  },
  pastel: {
    red: 'critical',
    yellow: 'yellow',
    orange: 'orange',
    green: 'positive',
    turquoise: 'turquoise',
    blueLight: 'blueLight',
    blueDark: 'blueDark',
    violet: 'violet',
    greyDark: 'secondary',
    greyLight: 'quaternary',
    disabled: 'disabled',
  },
};

const iconColors: {
  fill: Record<keyof typeof fillColorVariants, IconProps['color']>,
  pastel: Record<keyof typeof pastelColorVariants, IconProps['color']>
} = {
  fill: {
    red: 'white',
    yellow: 'white',
    orange: 'white',
    green: 'white',
    turquoise: 'white',
    blueLight: 'white',
    blueDark: 'white',
    violet: 'white',
    greyDark: 'inverted',
    greyLight: 'inverted',
    disabled: 'disabled',
  },
  pastel: {
    red: 'critical',
    yellow: 'yellow',
    orange: 'orange',
    green: 'positive',
    turquoise: 'turquoise',
    blueLight: 'blueLight',
    blueDark: 'blueDark',
    violet: 'violet',
    greyDark: 'secondary',
    greyLight: 'quaternary',
    disabled: 'disabled',
  },
};

const styles = stylex.create({
  base: {
    borderRadius: 4,
    borderStyle: 'none',
    borderWidth: 0,
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    width: 'fit-content',
  },
  rounded: {
    borderRadius: 999,
  },
  disabled: {
    opacity: 0.5,
  },
});

const sizeVariants = stylex.create({
  16: {
    gap: 2,
    height: 16,
    paddingBottom: 3.5,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2.5,
  },
  20: {
    gap: 4,
    height: 20,
    paddingBottom: 4,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 4,
  },
  32: {
    gap: 4,
    height: 32,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
  },
});

const fillColorVariants = stylex.create({
  red: { backgroundColor: $.surfaceCritical },
  yellow: { backgroundColor: $.accentSun60 },
  orange: { backgroundColor: $.accentPumpkin60 },
  green: { backgroundColor: $.surfacePositive },
  turquoise: { backgroundColor: $.accentTurquoise60 },
  blueLight: { backgroundColor: $.accentOcean60 },
  blueDark: { backgroundColor: $.accentSky60 },
  violet: { backgroundColor: $.accentPlum60 },
  greyDark: { backgroundColor: $.opacity48 },
  greyLight: { backgroundColor: $.opacity32 },
  disabled: { backgroundColor: $.buttonContentDisabled },
});

const pastelColorVariants = stylex.create({
  red: { backgroundColor: $.surfaceCriticalSilent },
  yellow: { backgroundColor: $.accentSun10 },
  orange: { backgroundColor: $.accentPumpkin10 },
  green: { backgroundColor: $.surfacePositiveMedium },
  turquoise: { backgroundColor: $.accentTurquoise10 },
  blueLight: { backgroundColor: $.accentOcean10 },
  blueDark: { backgroundColor: $.accentSky10 },
  violet: { backgroundColor: $.accentPlum10 },
  greyDark: { backgroundColor: $.opacity08 },
  greyLight: { backgroundColor: $.opacity06 },
  disabled: { backgroundColor: $.buttonContentDisabled },
});

const badgeTextStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  }
});

export { BadgeButton, BadgeButtonContext, type BadgeButtonProps };
